import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import FormSection from 'components/shared/FormSection';
import { showToast } from 'utils/helpers';
import {
  createWarrantyPackage,
  getWarrantyPackage,
  updateWarrantyPackage,
} from 'services/warranty-package.service';
import WarrantyPeriodInformation from './components/add/WarrantyPeriodInformation';
import WarrantyPeriodStatus from './components/add/WarrantyPeriodStatus';

const defaults = {
  package_name: '', product_months: null, accessory_coverage: 'UNDECLARED',
  accessory_months: null, same_product_period: false, description: '', is_active: 1,
};

const WarrantyPeriodAddPage = () => {
  const methods = useForm({ defaultValues: defaults });
  const { warranty_package_id } = useParams();
  const { pathname } = useLocation();
  const disabled = useMemo(() => pathname.includes('/detail/'), [pathname]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const loadDetail = useCallback(() => {
    setLoadFailed(false);
    if (!warranty_package_id) return methods.reset(defaults);
    setLoading(true);
    return getWarrantyPackage(warranty_package_id)
      .then((value) => methods.reset({
        ...defaults, ...value,
        same_product_period: value.accessory_coverage === 'PERIOD' && Number(value.accessory_months) === Number(value.product_months),
      }))
      .catch((error) => {
        setLoadFailed(true);
        showToast.error(error?.message || 'Không tải được gói bảo hành.');
      })
      .finally(() => setLoading(false));
  }, [warranty_package_id, methods]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const onSubmit = async (values) => {
    if (disabled || loading || loadFailed) return;
    try {
      const payload = {
        package_name: values.package_name.trim(),
        product_months: Number(values.product_months),
        accessory_coverage: values.accessory_coverage,
        accessory_months: values.accessory_coverage === 'PERIOD' ? Number(values.accessory_months) : null,
        description: values.description?.trim() || null,
        is_active: values.is_active ? 1 : 0,
      };
      if (warranty_package_id) {
        await updateWarrantyPackage(warranty_package_id, payload);
        showToast.success('Chỉnh sửa thành công.');
      } else {
        await createWarrantyPackage(payload);
        showToast.success('Thêm mới thành công.');
        methods.reset(defaults);
      }
    } catch (error) {
      showToast.error(error?.message || 'Có lỗi xảy ra.');
    }
  };

  const detailForm = [
    {
      id: 'information',
      title: 'Thông tin gói bảo hành',
      component: WarrantyPeriodInformation,
      fieldActive: ['package_name', 'product_months', 'accessory_coverage', 'accessory_months', 'description'],
    },
    { id: 'status', title: 'Trạng thái', component: WarrantyPeriodStatus },
  ];

  return (
    <FormProvider {...methods}>
      <FormSection detailForm={detailForm} onSubmit={onSubmit} disabled={disabled || loadFailed} loading={loading} disabledBtn={loadFailed} />
    </FormProvider>
  );
};

export default WarrantyPeriodAddPage;
